/**
 * CarouselRenderer — horizontal-scroll row of CardContentItems. Reuses
 * CardRenderer per item; the carousel itself just provides the layout
 * + scroll-snap + arrow affordances.
 *
 * For 380px embed bubbles each card sticks to ~280px wide, so a 3-card
 * carousel scrolls ~840px. scroll-snap-type lets the user flick to align
 * cards rather than scrolling to arbitrary offsets.
 */
import React, { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { CarouselContentItem } from '@wg/types';
import type { Renderer } from '../registry';
import CardRenderer from './CardRenderer';

const CarouselRenderer: Renderer = ({ item, ctx }) => {
  const { t } = useTranslation();
  const it = item as CarouselContentItem;
  const ref = useRef<HTMLDivElement | null>(null);

  const scrollBy = useCallback((dx: number) => {
    if (!ref.current) return;
    ref.current.scrollBy({ left: dx, behavior: 'smooth' });
  }, []);

  if (!it.items || it.items.length === 0) return null;

  return (
    <div
      className="wg-chat__carousel"
      role="region"
      aria-label={t('carousel.carousel')}
      aria-roledescription="carousel"
    >
      <button
        type="button"
        className="wg-chat__carousel-arrow wg-chat__carousel-arrow--prev"
        aria-label={t('common.previous')}
        onClick={() => scrollBy(-280)}
      >
        ‹
      </button>
      <div ref={ref} className="wg-chat__carousel-track">
        {it.items.map((c, i) => (
          <div key={i} className="wg-chat__carousel-item">
            <CardRenderer item={c} ctx={ctx} />
          </div>
        ))}
      </div>
      <button
        type="button"
        className="wg-chat__carousel-arrow wg-chat__carousel-arrow--next"
        aria-label={t('common.next')}
        onClick={() => scrollBy(280)}
      >
        ›
      </button>
    </div>
  );
};

export default CarouselRenderer;
